import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import CustomizeLayoutQuiz from 'component/layout_quiz/layout_quiz';
import { useAuth } from 'contexts/AuthContext';
import { useEffect, useMemo, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { setEyeColorDefault } from 'redux/authentication/user_detail';
import themes from 'styles/themes';
import { EQuestionId, EQuizId, ESubjectQuestion, quizContextType } from 'types/quiz';
import { CREATE_ANSWER } from '../../../graph_ql/getQuiz/mutation';
import { GET_QUESTION_DATA, GET_QUIZ_RESPONSE } from '../../../graph_ql/getQuiz/queries';
import { PATH } from '../../../routes/constant';
import Picker from 'component/ios_picker/iosPicker';
import { useStyles } from './styles';
import { STRINGS } from '../../../constants/strings';

export interface IEyeColorProps {}

const EyeColor = (props: { hideTitle?: boolean }) => {
    const { currentUser } = useAuth();
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();
    const classes = useStyles();
    const [quizData, setQuizData] = useState<any>(null);
    const [requiredQuestion, setRequiredQuestion] = useState(true);
    const [defaultValueEyeColor, setDefaultValueEyeColor] = useState<any>(null);
    const { userName } = useParams<{ userName: string }>();

    const [getQuizResponse, { loading: loadingQuiz }] = useLazyQuery(GET_QUIZ_RESPONSE, {
        onCompleted(data) {
            const answer = data.getQuizResponse?.answers?.eye_color_question;
            setRequiredQuestion(data.getQuizResponse?.questionsInfo[0]?.required);
            setDefaultValueEyeColor(answer);
            setQuizData(data.getQuizResponse);
        },
        notifyOnNetworkStatusChange: false,
    });

    const createAnswer = (input: {
        questionId: String;
        questionSubject: String;
        contextType: String;
        contextId: String;
        value: String;
        valueType: String;
        isSkipped?: boolean;
    }) => {
        const variables = {
            input: {
                ...input,
                ...(defaultValueEyeColor ? { id: defaultValueEyeColor.id } : {}),
            },
        };
        answerMutation({ variables });
    };
    const [answerMutation, { loading: loadingAnswer }] = useMutation(CREATE_ANSWER, {
        onCompleted(response) {
            onAnswerCompleted();
            dispatch(setEyeColorDefault(response.createAnswer));
        },
        onError(error) {
            console.log('Create Failed: ', error);
        },
    });

    const onAnswerCompleted = async () => {
        await getQuizResponse({
            variables: {
                input: {
                    quizId: EQuizId.EYE_COLOR,
                    contextType: quizContextType,
                    contextId: EQuizId.EYE_COLOR,
                    userId: currentUser.uid,
                },
            },
        });

        if (location.pathname === PATH.QUIZ_MANAGER) {
            history.push(`${PATH.QUIZ_MANAGER}/?signup=${Date.now()}`);
        }
    };

    const { data: eyeColorData, loading } = useQuery(GET_QUESTION_DATA, {
        variables: {
            input: {
                subject: ESubjectQuestion.EYE_COLOR,
                paging: { page: 0, size: 0 },
                enabled: true,
            },
        },
    });

    const listEyeColors = useMemo(
        () => (eyeColorData?.getQuestionData?.data || []).map((item) => ({ ...item, isSelected: false })),
        [eyeColorData?.getQuestionData?.data],
    );

    const [listEyeColorOptions, setListEyeColorOptions] = useState(listEyeColors || []);

    useEffect(() => {
        if (currentUser) {
            getQuizResponse({
                variables: {
                    input: {
                        quizId: EQuizId.EYE_COLOR,
                        contextType: quizContextType,
                        contextId: EQuizId.EYE_COLOR,
                        userId: currentUser.uid,
                    },
                },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser.uid]);

    useEffect(() => {
        let listEyeColorsCurrent: any = [];
        if (listEyeColors && listEyeColors.length > 0 && !loadingQuiz) {
            listEyeColorsCurrent = [...listEyeColors];
            const defaultValueIndex = listEyeColorsCurrent.findIndex(
                (item) => item.value.toLowerCase() === defaultValueEyeColor?.value?.toLowerCase(),
            );
            if (defaultValueIndex > -1) listEyeColorsCurrent[defaultValueIndex].isSelect = true;
            setListEyeColorOptions(listEyeColorsCurrent);
        }
    }, [defaultValueEyeColor, defaultValueEyeColor?.value, listEyeColors, loadingQuiz]);

    const [selectedValue, setSelectedValue] = useState('');

    const handleChangeValue = (value) => {
        if (value) {
            setSelectedValue(value);
        }
    };

    const input = {
        questionId: EQuestionId.EYE_COLOR,
        questionSubject: ESubjectQuestion.EYE_COLOR,
        contextType: quizContextType,
        contextId: EQuizId.EYE_COLOR,
        valueType: 'STRING',
    };

    const handleClickAnswer = () => {
        if (selectedValue) {
            createAnswer({ ...input, value: selectedValue });
        }
    };

    return (
        <CustomizeLayoutQuiz
            quizId={EQuizId.EYE_COLOR}
            title={Boolean(!userName) ? quizData?.subTitle : ''}
            titlePage={!props.hideTitle ? quizData?.title : null}
            handleAnswer={handleClickAnswer}
            isUpdate={location.pathname === PATH.EYE_COLOR}
            isDisplayContent={true}
            hideBottomNav={location.pathname === PATH.QUIZ_MANAGER}
            requiredQuestion={requiredQuestion}
            loading={loadingAnswer}
            containerLoading={loadingQuiz || loading}
            hideContinue={true}
            isHiddenAction={true}
        >
            <div className={classes.container}>
                <div className={`${classes.styleContainer} style-container`}>
                    <div className={classes.cardContainer}>
                        {listEyeColorOptions.length ? (
                            <Picker
                                optionGroups={{
                                    name: listEyeColorOptions.map((option) => {
                                        return {
                                            label: option.localizationTitle,
                                            value: option.value,
                                            id: option.id,
                                            questionId: EQuestionId.EYE_COLOR,
                                        };
                                    }),
                                }}
                                valueGroups={{
                                    name: selectedValue
                                        ? selectedValue
                                        : defaultValueEyeColor?.value
                                            ? defaultValueEyeColor?.value
                                            : '',
                                }}
                                onChange={(name, value) => {
                                    return handleChangeValue(value);
                                }}
                            />
                        ) : loadingQuiz ? (
                            <div style={{ height: 315 }} className={`${classes.flexCenter} picker-container`}>
                                <Spinner animation="border" style={{ marginLeft: 10, width: 18, height: 18 }} />
                            </div>
                        ) : (
                            <div style={{ height: 315 }} className={`${classes.flexCenter} picker-container`}></div>
                        )}
                    </div>
                    <button
                        type="submit"
                        className={classes.button}
                        style={{
                            color: themes.palette.common.white,
                            backgroundColor: selectedValue ? themes.palette.primary.main : '#A3A3A3',
                        }}
                        onClick={handleClickAnswer}
                        disabled={!selectedValue || loadingAnswer || loadingQuiz}
                    >
                        {location.pathname !== PATH.QUIZ_MANAGER ? STRINGS.UPDATE : STRINGS.SAVE}{' '}
                        {loadingAnswer && (
                            <Spinner animation="border" style={{ marginLeft: 10, width: 18, height: 18 }} />
                        )}
                    </button>
                </div>
            </div>
        </CustomizeLayoutQuiz>
    );
};
export default EyeColor;
